/**
 * 流向展开：四层树表，交互与口径移植产品原型 page.tsx 的「流向展开」区。
 *
 * 渲染用 antd Table 的树形数据（系统内表格风格），展开状态由本组件受控：
 *  - 「展开全部 / 收起全部」切换全局展开，同时清掉逐行展开记录
 *  - 搜索不区分大小写，命中本行保留整棵子树、否则只保留命中的后代，有搜索词时全部展开
 *  - 本仓（发支线合计）固定第一行；聚焦某环节且按「在仓合计」排序时，所有顶层行一起按该环节包裹数排
 */
import React from 'react';
import { Button, Input, Select, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Trans, useTranslation } from 'react-i18next';
import { ALL_STAGES, BATCH_STATUS_STYLE, C, isBagStage } from '../constants';
import type { BagRecord, DetailStage, StageKey } from '../types';
import {
    BRANCH_ID,
    cellDwellHours,
    maxDwellHours,
    totalBags,
    totalParcels,
    type FlowFilter,
    type FlowNode,
    type StageCell
} from '../lib/tree';
import { useFmt } from '../lib/useFmt';
import { I18N, useLabels } from '../lib/useLabels';
import HoverCard from './HoverCard';
import { LINK_CLS } from './StageCards';

export type SortKey = 'total' | 'stay' | 'code';

interface Props {
    tree: FlowNode[];
    now: number;
    overdueThresholdHours: number;
    focusStage: StageKey | 0;
    sort: SortKey;
    onSortChange: (s: SortKey) => void;
    onClearFocus: () => void;
    onOpenParcels: (scope: string, stage: DetailStage, filter: FlowFilter, total: number) => void;
    onOpenBags: (scope: string, stage: StageKey, bags: BagRecord[]) => void;
    /** 行末「明细」：该行 ②~④ 的全部在途包牌 */
    onOpenRowBags: (scope: string, bags: BagRecord[]) => void;
}

/** 列：流向 | ⓿ | ① | ② | ③ | ④ | 异常包裹 | 在仓合计 | 操作 */
const COL_W = { flow: 330, stage: { 0: 120, 1: 180, 2: 230, 3: 210, 4: 210, 6: 130 } as Record<StageKey, number>, total: 120, op: 60 };

interface RowMeta {
    depth: number;
    parentKey: string;
}

const FlowTable: React.FC<Props> = ({
    tree,
    now,
    overdueThresholdHours: th,
    focusStage,
    sort,
    onSortChange,
    onClearFocus,
    onOpenParcels,
    onOpenBags,
    onOpenRowBags
}) => {
    const { t } = useTranslation();
    const fmt = useFmt();
    const L = useLabels();
    const [query, setQuery] = React.useState('');
    const [expandedAll, setExpandedAll] = React.useState(false);
    const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

    const q = query.trim().toUpperCase();

    const shown = React.useMemo(() => {
        const rank = (n: FlowNode): string | number => {
            if (sort === 'code') return n.key;
            if (sort === 'stay') return -maxDwellHours(n.cells, now);
            return -(focusStage ? n.cells[focusStage].parcels : totalParcels(n.cells));
        };
        const cmp = (a: FlowNode, b: FlowNode) => {
            const ra = rank(a);
            const rb = rank(b);
            return typeof ra === 'string' ? ra.localeCompare(rb as string) : (ra as number) - (rb as number);
        };
        // 命中本行就保留整棵子树；否则只保留命中的后代
        const filterTree = (n: FlowNode): FlowNode | null => {
            if (!q || n.key.toUpperCase().includes(q)) return n;
            const kids = n.children.map(filterTree).filter((x): x is FlowNode => x !== null);
            return kids.length ? { ...n, children: kids } : null;
        };
        const sortTree = (n: FlowNode): FlowNode => ({ ...n, children: [...n.children].sort(cmp).map(sortTree) });
        const matched = tree.map(filterTree).filter((x): x is FlowNode => x !== null);
        return focusStage && sort === 'total'
            ? [...matched].sort(cmp).map(sortTree)
            : [
                  ...matched.filter((n) => n.id === BRANCH_ID).map(sortTree),
                  ...matched.filter((n) => n.id !== BRANCH_ID).sort(cmp).map(sortTree)
              ];
    }, [tree, q, sort, focusStage, now]);

    // 层级与父行名：depth 0 = 干线目的地 / 本仓，1 = WHS，2 = 批次（含未分批次），3 = WHS+线路首字母
    const { meta, openIds } = React.useMemo(() => {
        const m = new Map<string, RowMeta>();
        const open: string[] = [];
        const walk = (n: FlowNode, depth: number, parentKey: string) => {
            m.set(n.id, { depth, parentKey });
            if (n.children.length > 0 && (expandedAll || expanded.has(n.id) || !!q)) open.push(n.id);
            n.children.forEach((c) => walk(c, depth + 1, n.key));
        };
        shown.forEach((n) => walk(n, 0, ''));
        return { meta: m, openIds: open };
    }, [shown, expandedAll, expanded, q]);

    const toggle = (id: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const depthOf = (n: FlowNode) => meta.get(n.id)?.depth ?? 0;
    const scopeOf = (n: FlowNode) =>
        n.kind === 'root'
            ? t(`${I18N}.tree.branchTotal`) ?? ''
            : n.kind === 'real'
            ? `${meta.get(n.id)?.parentKey ?? ''} · ${L.tree.unbatched}`
            : n.key;

    // 鼠标移上「⏱ 最长停留」时显示的独立小卡片
    const dwellCard = (n: FlowNode, s: StageKey, c: StageCell, hours: number) => {
        const bag = isBagStage(s);
        const over = hours > th;
        const stage = L.stageTitle(s);
        return (
            <>
                <div style={{ fontWeight: 600, color: C.text1 }}>
                    {t(bag ? `${I18N}.flow.dwellTitleBag` : `${I18N}.flow.dwellTitleParcel`, {
                        flow: n.key,
                        stage,
                        dwell: fmt.dwell(hours)
                    })}
                </div>
                <div>
                    <Trans
                        i18nKey={bag ? `${I18N}.flow.dwellBodyBag` : `${I18N}.flow.dwellBodyParcel`}
                        values={{
                            stage,
                            dwell: fmt.dwell(hours),
                            cmp: over ? t(`${I18N}.flow.cmpOver`) : t(`${I18N}.flow.cmpUnder`),
                            th,
                            n: fmt.num(c.parcels),
                            m: fmt.num(c.bags.length)
                        }}
                        components={[<b style={{ color: over ? C.danger : C.text1 }} />]}
                    />
                </div>
                <div>
                    <b style={{ color: C.text1 }}>{t(`${I18N}.alerts.possibleImpact`)}</b> · {L.impact(s)}
                </div>
                <div>
                    <b style={{ color: C.text1 }}>{t(`${I18N}.alerts.suggestedAction`)}</b> · {L.action(s)}
                </div>
            </>
        );
    };

    const renderCell = (n: FlowNode, s: StageKey) => {
        const c = n.cells[s];
        const scope = scopeOf(n);
        const mock = !!n.mock; // 示例批次：没有真实数据，数字不可点
        const hours = cellDwellHours(c, now);
        const has = c.parcels > 0 || c.bags.length > 0;
        const over = hours > th;
        const num = (label: string, kind: 'parcel' | 'bag') =>
            mock ? (
                <span>{label}</span>
            ) : (
                <span
                    className={LINK_CLS}
                    onClick={() =>
                        kind === 'parcel' ? onOpenParcels(scope, s, n.filter, c.parcels) : onOpenBags(scope, s, c.bags)
                    }
                >
                    {label}
                </span>
            );
        return (
            <div>
                <div className="flex flex-wrap items-center gap-x-1.5">
                    {num(fmt.pcs(c.parcels), 'parcel')}
                    {s === 0 && (
                        <>
                            <span>/</span>
                            <span>{fmt.num(c.bigBags)}</span>
                        </>
                    )}
                    {isBagStage(s) && (
                        <>
                            <span>/</span>
                            {num(fmt.num(c.bags.length), 'bag')}
                            <span>/</span>
                            <span style={{ color: C.text3 }}>0</span>
                        </>
                    )}
                    {has && !mock && (
                        <HoverCard card={dwellCard(n, s, c, hours)} accent={over ? C.danger : C.text3}>
                            <span
                                className="ml-0.5 cursor-default rounded text-[11.5px]"
                                style={{
                                    padding: '1px 5px',
                                    color: over ? C.danger : C.text3,
                                    background: over ? C.dangerBg : 'transparent',
                                    fontWeight: over ? 700 : 400
                                }}
                            >
                                ⏱ {fmt.dwell(hours)}
                            </span>
                        </HoverCard>
                    )}
                </div>
                {s === 1 && c.err > 0 && !mock && (
                    <span
                        onClick={() => onOpenParcels(scope, 5, n.filter, c.err)}
                        className="mt-0.5 inline-block cursor-pointer rounded text-[11.5px] font-bold"
                        style={{ padding: '1px 5px', color: C.danger, background: C.dangerBg }}
                    >
                        ⚠ {t(`${I18N}.flow.errBadge`, { n: fmt.num(c.err) })}
                    </span>
                )}
                {s === 2 && c.stragParcels > 0 && !mock && (
                    <span
                        className="mt-0.5 inline-block rounded text-[11.5px] font-bold"
                        style={{ padding: '1px 5px', color: C.danger, background: C.dangerBg }}
                    >
                        ⚠ {t(`${I18N}.flow.stragBadge`, { n: fmt.num(c.stragParcels) })}
                    </span>
                )}
            </div>
        );
    };

    // 每层的行样式照原型：顶层上边一道浅紫线，下层浅紫底 + 左侧按层级加粗的紫色竖条，示例行置灰
    const baseCellStyle = (n: FlowNode): React.CSSProperties =>
        depthOf(n) === 0 ? { borderTop: `1px solid ${C.level2Accent}` } : {};
    const colHot = (s: StageKey): React.CSSProperties => (focusStage === s ? { background: C.primaryBg } : {});

    const columns: ColumnsType<FlowNode> = [
        {
            key: 'flow',
            title: t(`${I18N}.flow.colFlow`),
            width: COL_W.flow,
            onCell: (n) => {
                const depth = depthOf(n);
                return {
                    style: {
                        ...baseCellStyle(n),
                        paddingLeft: 6 + depth * 18,
                        boxShadow: depth === 0 ? undefined : `inset ${3 * depth}px 0 0 ${C.level1Accent}`
                    }
                };
            },
            render: (_, n) => {
                const hasKids = n.children.length > 0;
                const open = openIds.includes(n.id);
                return (
                    <div
                        onClick={() => hasKids && toggle(n.id)}
                        className={`flex select-none items-center gap-1 ${hasKids ? 'cursor-pointer' : ''}`}
                    >
                        <span style={{ width: 12, color: C.text3, fontSize: 11, flexShrink: 0 }}>
                            {hasKids ? (open ? '▾' : '▸') : ''}
                        </span>
                        <span className="truncate" style={n.kind === 'real' ? { fontStyle: 'italic' } : undefined}>
                            {n.key}
                        </span>
                        {n.kind === 'batch' && n.batchStatus && (
                            <span
                                className="shrink-0 rounded text-[11.5px]"
                                style={{
                                    padding: '0 6px',
                                    color: BATCH_STATUS_STYLE[n.batchStatus].fg,
                                    background: BATCH_STATUS_STYLE[n.batchStatus].bg
                                }}
                            >
                                {L.batchStatus(n.batchStatus)}
                            </span>
                        )}
                        {n.mock && (
                            <span className="shrink-0 text-[11px]" style={{ color: C.text3 }}>
                                {t(`${I18N}.tree.mockTag`)}
                            </span>
                        )}
                        {n.kind === 'real' && (
                            <span className="shrink-0 text-[11px]" style={{ color: C.text3 }}>
                                {t(`${I18N}.tree.batchOffline`)}
                            </span>
                        )}
                    </div>
                );
            }
        },
        ...ALL_STAGES.map((s) => ({
            key: `s${s}`,
            title: L.stageTitle(s),
            width: COL_W.stage[s],
            onHeaderCell: () => ({ style: colHot(s) }),
            onCell: (n: FlowNode) => ({ style: { ...baseCellStyle(n), ...colHot(s) } }),
            render: (_: unknown, n: FlowNode) => renderCell(n, s)
        })),
        {
            key: 'total',
            title: t(`${I18N}.flow.colTotal`),
            width: COL_W.total,
            onCell: (n) => ({ style: baseCellStyle(n) }),
            render: (_, n) => (
                <div className={depthOf(n) === 0 ? 'font-semibold' : 'font-medium'}>
                    {fmt.pcs(totalParcels(n.cells))} / {fmt.num(totalBags(n.cells))} / <span style={{ color: C.text3 }}>0</span>
                </div>
            )
        },
        {
            key: 'op',
            title: t(`${I18N}.flow.colAction`),
            width: COL_W.op,
            align: 'right',
            onCell: (n) => ({ style: baseCellStyle(n) }),
            render: (_, n) =>
                !n.mock && (
                    <Button
                        size="small"
                        onClick={() => onOpenRowBags(scopeOf(n), [2, 3, 4].flatMap((s) => n.cells[s as StageKey].bags))}
                    >
                        {t(`${I18N}.flow.detail`)}
                    </Button>
                )
        }
    ];

    return (
        <div className="mb-4 rounded-md bg-white" style={{ border: `1px solid ${C.border}` }}>
            <div className="flex flex-wrap items-center gap-3 px-3 pt-3">
                <div className="text-[15px] font-semibold" style={{ color: C.text1 }}>
                    {t(`${I18N}.flow.title`)}
                </div>
                <span className="text-[12.5px]" style={{ color: C.text3 }}>
                    {focusStage
                        ? t(`${I18N}.flow.hintFocus`, { stage: L.stageTitle(focusStage) })
                        : t(`${I18N}.flow.hintDefault`)}
                </span>
                <span className="flex-1" />
                <Input
                    size="small"
                    allowClear
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t(`${I18N}.flow.searchPlaceholder`) ?? ''}
                    style={{ width: 190 }}
                />
                <Select<SortKey>
                    size="small"
                    value={sort}
                    onChange={onSortChange}
                    style={{ minWidth: 150 }}
                    options={[
                        { value: 'total', label: t(`${I18N}.flow.sortTotal`) },
                        { value: 'stay', label: t(`${I18N}.flow.sortStay`) },
                        { value: 'code', label: t(`${I18N}.flow.sortCode`) }
                    ]}
                />
                <Button
                    size="small"
                    onClick={() => {
                        setExpandedAll((v) => !v);
                        setExpanded(new Set());
                    }}
                >
                    {expandedAll ? t(`${I18N}.flow.collapseAll`) : t(`${I18N}.flow.expandAll`)}
                </Button>
                <Button size="small" onClick={onClearFocus}>
                    {t(`${I18N}.flow.clearFocus`)}
                </Button>
            </div>

            <div className="px-3 pb-3 pt-2">
                <Table<FlowNode>
                    size="small"
                    rowKey="id"
                    columns={columns}
                    dataSource={shown}
                    pagination={false}
                    scroll={{ x: 1620 }}
                    expandable={{
                        expandedRowKeys: openIds,
                        expandIcon: () => null,
                        indentSize: 0
                    }}
                    onRow={(n) => {
                        const depth = depthOf(n);
                        return {
                            style: {
                                fontSize: 13,
                                background: n.mock ? '#FBFAFD' : depth === 0 ? undefined : C.level1Bg,
                                color: n.mock ? C.text3 : depth === 0 ? C.text1 : C.text2
                            }
                        };
                    }}
                    locale={{ emptyText: t(`${I18N}.flow.empty`) }}
                />
            </div>
            <div
                className="px-3 pb-3 text-[12.5px]"
                style={{ color: C.text3, borderTop: `1px solid ${C.borderLight}`, paddingTop: 6 }}
            >
                {t(`${I18N}.flow.footer`, { th })}
            </div>
        </div>
    );
};

export default FlowTable;
